import Image from 'next/image'
import {FC} from 'react'

import {useHelpCenterContent} from '@/content/helpCenter'
import {Link, useTranslation} from '@/i18n'
import {theme} from '@/theme/theme'
import {formatDate} from '@/utils/formatDate'
import {useLanguage} from '@/utils/i18n/language'
import {routeTo} from '@/utils/router'

import {Text, TextVariant} from '../common/content/Text'

type AuthorProps = {
  name: string
  role: string
  photoSrc: string
  photoAlt: string
  slug?: string
  date?: string
}

export const Author: FC<React.PropsWithChildren<AuthorProps>> = ({
  name,
  role,
  photoSrc,
  photoAlt,
  slug,
  date,
}) => {
  const {t} = useTranslation('page')
  const language = useLanguage()
  const {writtenByText} = useHelpCenterContent()

  return (
    <>
      <div className="author">
        <div className="photo">
          <Image
            src={photoSrc}
            alt={photoAlt}
            style={{objectFit: 'contain'}}
            width={44}
            height={44}
            quality={100}
          />
        </div>
        <div>
          <Text>
            {t(writtenByText)}{' '}
            {slug ? (
              <Link href={routeTo.aboutUs.teamMember(slug)} legacyBehavior>
                <a target="_blank">
                  <strong className="underline">{name}</strong>
                </a>
              </Link>
            ) : (
              <strong>{name}</strong>
            )}
          </Text>
          <div className="role">
            <Text variant={TextVariant.text14} color={theme.palette.text.light}>
              {role}
              {date && (
                <>
                  {' · '}
                  <time dateTime={date}>{formatDate(date, language)}</time>
                </>
              )}
            </Text>
          </div>
        </div>
      </div>

      <style jsx>{`
        .author {
          display: flex;
          align-items: center;
        }
        .photo {
          position: relative;
          width: 44px;
          height: 44px;
          flex: 0 0 44px;
          border-radius: 4px;
          overflow: hidden;
          margin-right: ${theme.spacing[3]};
        }
        .role {
          margin-top: calc(${theme.spacing[1]} / 2);
        }
      `}</style>
    </>
  )
}
